import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// CRUD for the user's medications list. Active rows are those with
// `stopped_at` IS NULL; stopped ones stay around so the user keeps the
// history but they're filtered out of the default "current meds" view.
export function useMedications({ includeStopped = false } = {}) {
  const { user } = useAuth()
  const [medications, setMedications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    let query = supabase
      .from('medications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
    if (!includeStopped) query = query.is('stopped_at', null)
    const { data, error: err } = await query
    if (err) setError(err.message)
    else setMedications(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, includeStopped])

  useEffect(() => { fetch() }, [fetch])

  const create = async (values) => {
    if (!user) return null
    const { data, error: err } = await supabase
      .from('medications')
      .insert({ ...values, user_id: user.id })
      .select('*')
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    await fetch()
    return data
  }

  const update = async (id, patch) => {
    const { error: err } = await supabase
      .from('medications')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  const remove = async (id) => {
    const { error: err } = await supabase.from('medications').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  return { medications, loading, error, create, update, remove, refetch: fetch }
}
